import client from "@/api/openapi-ts";
import { defineStore, acceptHMRUpdate } from "pinia";
import { ref } from "vue";
import { MinMaxDensity } from "@/types/api/Map";
import layerService from "@/helpers/layerManager";
import { debounce } from "@/helpers/debounce";
import type { Deck } from "@deck.gl/core";
import type { Map } from "maplibre-gl";

type ViewState = {
  altitude?: number;
  bearing?: number;
  height?: number;
  latitude: number;
  longitude: number;
  maxPitch?: number;
  minPitch?: number;
  minZoom?: number;
  normalize?: boolean;
  pitch?: number;
  position?: number[];
  width?: number;
  zoom: number;
};

const initialViewState = () => ({
  longitude: -97.2,
  latitude: 44.33,
  zoom: 2.5,
});

type DeckInstance = Deck & {
  _map: Map & {
    bounds: number[];
  };
};

export const useMapStore = defineStore("map", () => {
  const viewState = ref<ViewState>(initialViewState());
  const cartoLoading = ref(false);
  const loadingAddressCoords = ref(false);
  const loadingAutocomplete = ref(false);
  const minMaxDensity = ref<MinMaxDensity>();
  const currentMapQuery = ref("");
  const queryToken = ref("");
  const autocompleteResults = ref<string[]>([]);
  const mapBounds = ref<number[]>([]);

  const setViewState = (view: ViewState) => {
    const setDelayedViewState = debounce((v: ViewState) => {
      viewState.value = v;
      const instance = layerService.deckInstance as
        DeckInstance | null | undefined;
      const bounds = instance?._map?.bounds;
      mapBounds.value = Array.isArray(bounds) ? bounds : [];
    }, 500);
    setDelayedViewState(view);
    (layerService.deckInstance as DeckInstance | null | undefined)?.setProps({
      viewState: { ...view },
    });
  };
  const setViewStateInstant = (view: ViewState) => {
    (layerService.deckInstance as DeckInstance | null | undefined)?.setProps({
      viewState: { ...view },
    });
    viewState.value = view;
  };
  const setAutocompleteResults = (addresses: string[]) => {
    autocompleteResults.value = addresses;
  };
  const setCartoLoading = (loading: boolean) => {
    cartoLoading.value = loading;
  };

  return {
    viewState,
    cartoLoading,
    loadingAddressCoords,
    loadingAutocomplete,
    minMaxDensity,
    currentMapQuery,
    queryToken,
    autocompleteResults,
    mapBounds,
    setViewState,
    setViewStateInstant,
    setCartoLoading,
    setAutocompleteResults,
    getMinMax: async (params: { metaAudienceId: number; geoType: string }) => {
      const { metaAudienceId, geoType } = params;
      try {
        const { data, error } = await client.GET(
          "/api/v1/meta_audiences/{meta_audience_id}/geography_density_range",
          {
            params: {
              path: { meta_audience_id: metaAudienceId },
              query: { geo_type: geoType } as never,
            },
          },
        );
        if (error) {
          throw error;
        }
        minMaxDensity.value = data as MinMaxDensity;
      } catch (e) {
        console.log("map density error", e);
        minMaxDensity.value = undefined;
      }
    },
    authorizeMapQuery: async (payload: { sql: string }) => {
      if (payload.sql === currentMapQuery.value) {
        return true;
      }
      try {
        const { data, error } = await client.POST("/api/v1/maps", {
          body: payload as never,
        });
        if (error) {
          throw error;
        }
        const result = data as { sql: string; token: string };
        currentMapQuery.value = result.sql;
        queryToken.value = result.token;
        return true;
      } catch (e) {
        console.log("map authorization error", e);
        return false;
      }
    },
    getAddressCoords: async (address: string) => {
      loadingAddressCoords.value = true;
      try {
        const { data, error } = await client.GET("/api/v1/maps/places", {
          params: { query: { address } as never },
        });
        if (error) {
          throw error;
        }
        return data;
      } finally {
        loadingAddressCoords.value = false;
      }
    },
    getAddressAutocomplete: async (address: string) => {
      loadingAutocomplete.value = true;
      try {
        const { data, error } = await client.GET("/api/v1/maps/autocomplete", {
          params: { query: { address } as never },
        });
        if (error) {
          throw error;
        }
        const result = data as { addresses?: string[] } | undefined;
        setAutocompleteResults(result?.addresses ?? []);
      } finally {
        loadingAutocomplete.value = false;
      }
    },
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useMapStore, import.meta.hot));
}
